/**
 * First-login walkthrough for bulk-invited installers.
 *
 * Fires when:
 *   - user.role === 'installer'
 *   - user.bulk_invited === true (set by the Bulk Invite modal on create)
 *   - !user.onboarding_completed_at (never dismissed / finished before)
 *
 * Copy is tuned for a brand-new hire who has never seen the portal —
 * short, friendly, no jargon. Each step highlights one thing they can do
 * *right now* without leaving the dashboard.
 *
 * The tour is dismissable at any step. Both "Finish" and "Skip" hit the
 * backend so the tour never fires again, even if the installer refreshes
 * mid-way.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  X, ArrowRight, CheckCircle2, Layers, MessageCircle, GraduationCap, Sparkles,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import API_URL from '@/config/api';
import logger from '@/utils/logger';

// Tour copy — kept in a const so the whole thing is data-driven.
// Order matters: welcome first, brand tiles second (they map directly to
// what the installer sees behind the modal), messaging third, training
// last so the flow ends on a "here's how you level up" note.
const STEPS = [
  {
    id: 'welcome',
    icon: Sparkles,
    accent: '#7A0B7A',
    title: (name) => `Welcome to Ox Resin${name ? `, ${name}` : ''}!`,
    body: (
      <>
        <p className="text-sm text-gray-700">
          We&apos;ll show you round in under a minute so you know exactly where
          everything is. You can skip any time — this tour only shows once.
        </p>
        <p className="text-xs text-gray-500 mt-3">
          <strong>Tip:</strong> the portal is designed to work on your phone on-site
          — everything you need is one tap away.
        </p>
      </>
    ),
  },
  {
    id: 'brands',
    icon: Layers,
    accent: '#2F5E96',
    title: () => 'The three brand tiles',
    body: (grantedBrandsLabel) => (
      <>
        <p className="text-sm text-gray-700">
          Each tile opens the tools for one brand — <strong>ProBound</strong> for
          resin bound driveways, <strong>ProEpoxy</strong> for epoxy floors, and
          <strong> ProDecor</strong> for decorative walling.
        </p>
        <p className="text-sm text-gray-700 mt-2">
          {grantedBrandsLabel ? (
            <>Your account is pre-loaded with <strong>{grantedBrandsLabel}</strong>. Tap the tile to price a job, look up materials or download resources.</>
          ) : (
            <>Ask your Ox Resin contact to grant you access to the brands you install — the tiles will unlock automatically.</>
          )}
        </p>
      </>
    ),
  },
  {
    id: 'quote',
    icon: CheckCircle2,
    accent: '#0F7A45',
    title: () => 'Build a live quote in minutes',
    body: () => (
      <>
        <p className="text-sm text-gray-700">
          Inside each brand you&apos;ll find a <strong>Live Quote</strong> tool. Punch
          in the job details and get a fully-priced customer quote using the
          live material tiers — no spreadsheet gymnastics.
        </p>
        <p className="text-xs text-gray-500 mt-3">
          Your company name, logo and price band are already set up — quotes
          go out under your brand automatically.
        </p>
      </>
    ),
  },
  {
    id: 'messages',
    icon: MessageCircle,
    accent: '#D35500',
    title: () => "We'll message you here",
    body: () => (
      <>
        <p className="text-sm text-gray-700">
          When the Ox Resin team sends you a message — a course reminder,
          a new material tier, an important update — you&apos;ll get a text or
          email <em>and</em> see it right here in the portal.
        </p>
        <p className="text-xs text-gray-500 mt-3">
          Nothing important slips through, even on your busiest day.
        </p>
      </>
    ),
  },
  {
    id: 'training',
    icon: GraduationCap,
    accent: '#0F7A45',
    title: () => 'Book training, earn approval',
    body: () => (
      <>
        <p className="text-sm text-gray-700">
          The <strong>ProTraining</strong> tile lists our upcoming courses.
          Completing a course unlocks new brand tools and puts you on the way
          to becoming an <strong>Ox Approved Installer</strong>.
        </p>
        <p className="text-xs text-gray-500 mt-3">
          Approved installers get priority leads. That&apos;s the goal.
        </p>
      </>
    ),
  },
];

function firstName(fullName) {
  if (!fullName) return '';
  return String(fullName).trim().split(' ')[0];
}

function grantedBrandsLabel(access) {
  const map = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };
  const granted = Object.entries(map)
    .filter(([k]) => access && access[k])
    .map(([, v]) => v);
  if (granted.length === 0) return null;
  if (granted.length === 1) return granted[0];
  if (granted.length === 2) return `${granted[0]} and ${granted[1]}`;
  return `${granted.slice(0, -1).join(', ')} and ${granted[granted.length - 1]}`;
}

export default function OnboardingTour() {
  const { user, checkAuth } = useAuth();
  // `dismissed` is a local guard so hitting Skip doesn't briefly re-open
  // the tour before checkAuth() finishes and clears bulk_invited.
  const [dismissed, setDismissed] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const eligible = useMemo(() => {
    if (!user) return false;
    if (user.role !== 'installer') return false;
    if (!user.bulk_invited) return false;
    if (user.onboarding_completed_at) return false;
    return true;
  }, [user]);

  // Reset step whenever the user changes (avoids stale mid-tour state
  // for a returning installer).
  useEffect(() => { setStep(0); }, [user?.user_id]);

  const markComplete = async () => {
    setBusy(true);
    setDismissed(true);
    try {
      const r = await fetch(`${API_URL}/api/users/me/onboarding-complete`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Refresh user in context so the tour never re-opens on this
      // session either (belt & braces on top of `dismissed`).
      await checkAuth();
    } catch (e) {
      // Non-blocking — the tour is closed regardless. Log and move on.
      logger.error('Failed to mark onboarding complete', e);
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (step >= STEPS.length - 1) {
      markComplete();
      return;
    }
    setStep((s) => s + 1);
  };

  if (!eligible || dismissed) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const name = firstName(user?.name);
  const brandsLabel = grantedBrandsLabel(user?.access);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="onboarding-tour"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Accent header */}
        <div
          className="relative px-6 pt-6 pb-4 text-white"
          style={{ backgroundColor: current.accent }}
        >
          <button
            onClick={markComplete}
            disabled={busy}
            className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/20 transition"
            data-testid="onboarding-close"
            aria-label="Skip the tour"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center mb-3">
            <Icon className="w-6 h-6" />
          </div>
          <h2
            className="text-lg font-bold leading-tight"
            data-testid={`onboarding-title-${current.id}`}
            // Some titles contain the apostrophe entity `We&apos;ll` — using
            // dangerouslySetInnerHTML would be overkill; React renders the
            // literal string fine here since we pre-encoded correctly above.
          >
            {typeof current.title === 'function' ? current.title(name) : current.title}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 py-5" data-testid={`onboarding-body-${current.id}`}>
          {typeof current.body === 'function' ? current.body(brandsLabel) : current.body}
        </div>

        {/* Progress dots + CTAs */}
        <div className="px-6 pb-5 flex items-center justify-between">
          <div className="flex items-center gap-1.5" aria-label="Tour progress">
            {STEPS.map((s, i) => (
              <span
                key={s.id}
                className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6' : 'w-1.5'}`}
                style={{ backgroundColor: i <= step ? current.accent : '#e5e7eb' }}
                data-testid={`onboarding-dot-${i}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={markComplete}
              disabled={busy}
              className="text-xs font-semibold text-gray-500 hover:text-gray-800"
              data-testid="onboarding-skip"
            >
              Skip tour
            </button>
            <Button
              onClick={next}
              disabled={busy}
              className="text-white font-semibold px-4"
              style={{ backgroundColor: current.accent }}
              data-testid={step >= STEPS.length - 1 ? 'onboarding-finish' : 'onboarding-next'}
            >
              {step >= STEPS.length - 1 ? "Let's go" : 'Next'}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
